import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GitBranch, Clock, Layers, CheckCircle, AlertCircle, Circle, Workflow } from 'lucide-react';
import api from '../services/api';

const STATUS_CONFIG = {
  completed: { label: 'Completed', icon: CheckCircle, cls: 'bg-emerald-100 text-emerald-700' },
  failed:    { label: 'Failed',    icon: AlertCircle, cls: 'bg-red-100 text-red-700' },
  running:   { label: 'Running',   icon: Clock,       cls: 'bg-amber-100 text-amber-700' },
  idle:      { label: 'Not run',   icon: Circle,      cls: 'bg-gray-100 text-gray-500' },
};

function derivePipelineStatus(elements) {
  const nodes = elements.filter(e => e.position);
  if (!nodes.length) return 'idle';
  if (nodes.some(n => n.data?.status === 'error'))   return 'failed';
  if (nodes.some(n => n.data?.status === 'running')) return 'running';
  if (nodes.some(n => n.data?.results))              return 'completed';
  return 'idle';
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

const SkeletonCard = () => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden animate-pulse">
    <div className="h-1 bg-gray-200 w-full" />
    <div className="p-5 space-y-3">
      <div className="flex justify-between items-start">
        <div className="h-4 bg-gray-200 rounded w-2/5" />
        <div className="h-5 bg-gray-100 rounded-full w-20" />
      </div>
      <div className="h-3 bg-gray-100 rounded w-1/4" />
      <div className="pt-3 border-t border-gray-50 flex gap-4">
        <div className="h-3 bg-gray-100 rounded w-16" />
        <div className="h-3 bg-gray-100 rounded w-16" />
        <div className="h-3 bg-gray-100 rounded w-24 ml-auto" />
      </div>
    </div>
  </div>
);

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-24 text-center">
    <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-4">
      <GitBranch className="w-8 h-8 text-blue-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-800 mb-1">No pipelines yet</h3>
    <p className="text-sm text-gray-400 max-w-xs">
      Open a project and create a pipeline to get started.
    </p>
  </div>
);

const STATUS_ACCENT = {
  completed: 'bg-emerald-500',
  failed:    'bg-red-500',
  running:   'bg-amber-400',
  idle:      'bg-gray-200',
};

const PipelineCard = ({ pipeline }) => {
  const navigate    = useNavigate();
  const nodes       = pipeline.elements.filter(e => e.position);
  const edges       = pipeline.elements.filter(e => e.source);
  const status      = derivePipelineStatus(pipeline.elements);
  const cfg         = STATUS_CONFIG[status];
  const StatusIcon  = cfg.icon;

  return (
    <div
      onClick={() => navigate(`/pipeline/${pipeline._id}`)}
      className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-blue-200 transition-all duration-200 cursor-pointer group flex flex-col overflow-hidden"
    >
      {/* colour accent bar */}
      <div className={`h-1 w-full ${STATUS_ACCENT[status]}`} />

      <div className="p-5 flex flex-col flex-1">
        {/* name + badge */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <h2 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors leading-snug">
            {pipeline.name}
          </h2>
          <span className={`flex-shrink-0 flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cfg.cls}`}>
            <StatusIcon className="w-3 h-3" />
            {cfg.label}
          </span>
        </div>

        {/* project name */}
        {pipeline.project?.name && (
          <p className="text-xs text-gray-400 mb-3">{pipeline.project.name}</p>
        )}

        <div className="flex-1" />

        {/* footer stats */}
        <div className="flex items-center gap-3 pt-3 border-t border-gray-50 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <Layers className="w-3 h-3" />
            {nodes.length} node{nodes.length !== 1 ? 's' : ''}
          </span>
          <span className="flex items-center gap-1">
            <GitBranch className="w-3 h-3" />
            {edges.length} edge{edges.length !== 1 ? 's' : ''}
          </span>
          <span className="flex items-center gap-1 ml-auto">
            <Clock className="w-3 h-3" />
            {formatDate(pipeline.updatedAt)}
          </span>
        </div>
      </div>
    </div>
  );
};

const Pipelines = () => {
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('All');

  useEffect(() => {
    api.get('/pipelines/all')
      .then(res => setPipelines(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const statusCounts = {
    All:       pipelines.length,
    Completed: pipelines.filter(p => derivePipelineStatus(p.elements) === 'completed').length,
    Failed:    pipelines.filter(p => derivePipelineStatus(p.elements) === 'failed').length,
    'Not run': pipelines.filter(p => derivePipelineStatus(p.elements) === 'idle').length,
  };

  const filtered = filter === 'All'
    ? pipelines
    : pipelines.filter(p => {
        const s = derivePipelineStatus(p.elements);
        if (filter === 'Completed') return s === 'completed';
        if (filter === 'Failed')    return s === 'failed';
        if (filter === 'Not run')   return s === 'idle';
        return true;
      });

  const totalNodes = pipelines.reduce(
    (acc, p) => acc + p.elements.filter(e => e.position).length, 0
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-b from-white to-blue-50 border-b border-gray-100 px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <Workflow className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Pipelines</h1>
          </div>
          <p className="text-sm text-gray-500 mb-6 ml-10">
            {pipelines.length === 0
              ? 'No pipelines created yet'
              : `${pipelines.length} pipeline${pipelines.length !== 1 ? 's' : ''} across all projects`}
          </p>

          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 text-sm shadow-sm">
              <Workflow className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-gray-500">Total</span>
              <span className="font-semibold text-gray-900">{pipelines.length}</span>
            </div>
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 text-sm shadow-sm">
              <Layers className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-gray-500">Total nodes</span>
              <span className="font-semibold text-gray-900">{totalNodes}</span>
            </div>
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 text-sm shadow-sm">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-gray-500">Completed</span>
              <span className="font-semibold text-emerald-600">{statusCounts.Completed}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="bg-white border-b border-gray-100 px-6">
        <div className="max-w-5xl mx-auto flex gap-1 overflow-x-auto">
          {Object.entries(statusCounts).map(([tab, count]) => {
            const active = tab === filter;
            return (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 transition-colors duration-150 ${
                  active
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
                }`}
              >
                {tab}
                <span className={`text-xs rounded-full px-1.5 py-0.5 font-medium ${
                  active ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map(pipeline => (
              <PipelineCard key={pipeline._id} pipeline={pipeline} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Pipelines;
